import React, { useState, useEffect, useRef } from 'react';
import uniqueId from '../utilities/unique-id';
import last from 'lodash/last'

const formatNumber = (quantity, roundUp = false) => new Intl.NumberFormat().format(
    roundUp ? Math.ceil(quantity) : quantity,
);

const NumberInput = ({
    i18n,
    width = 'w-40',
    min = -100,
    max = 100,
    defaultValue = '0',
    name,
    required = false,
    text,
    onChange,
    prefix = null,
    suffix = null,
    readOnly = false,
}) => {
    defaultValue = parseFloat(defaultValue);

    const applyPrefixes = string => {
        if (!string) {
            return string;
        }

        if (prefix) {
            return prefix + string;
        }

        if (suffix) {
            return string + suffix;
        }

        return string;
    }

    const id = uniqueId()
    const label = text || i18n[name]
    const input = useRef(null)
    const [rawQuantity, setRawQuantity] = useState(defaultValue);
    const [quantity, setQuantity] = useState(applyPrefixes(formatNumber(defaultValue)));
    const [canIncrease, setCanIncrease] = useState(true);
    const [canReduce, setCanReduce] = useState(false);
    const [focused, setFocused] = useState(false);

    const updateQuantityTo = quantity => {
        setRawQuantity(quantity)

        setQuantity(applyPrefixes(formatNumber(quantity)));
    }

    const increase = shiftKey => {
        const newQuantity = rawQuantity + (shiftKey ? 10 : 1);

        if (newQuantity > max) {
            return false;
        }

        updateQuantityTo(newQuantity)
    }

    const reduce = shiftKey => {
        const newQuantity = rawQuantity - (shiftKey ? 10 : 1);

        if (newQuantity < min) {
            return false;
        }

        updateQuantityTo(newQuantity)
    }

    const parseInput = value => {
        let newQuantity = value;

        newQuantity = newQuantity.replace(/[^0-9.]/g, '');

        if (last(newQuantity) === '.') {
            return setQuantity(newQuantity)
        }

        newQuantity = parseFloat(newQuantity);

        if (newQuantity === 0) {
            return setQuantity(value)
        }

        if (isNaN(newQuantity)) {
            return updateQuantityTo('');
        }

        if (newQuantity < min) {
            return false;
        }

        if (newQuantity > max) {
            return false;
        }

        if (readOnly) {
            return false;
        }

        updateQuantityTo(newQuantity);
    }

    const updateExternals = () => {
        if (rawQuantity <= min) {
            setCanIncrease(true);
            setCanReduce(false);
        } else if (rawQuantity >= max) {
            setCanIncrease(false);
            setCanReduce(true);
        } else {
            setCanIncrease(true)
            setCanReduce(true)
        }
    }

    const keyUp = ({ key, shiftKey }) => {
        if (key === 'ArrowUp') {
            return increase(shiftKey);
        }

        if (key === 'ArrowDown') {
            return reduce(shiftKey);
        }
    }

    const defaultToOne = () => {
        if (!rawQuantity) {
            updateQuantityTo(0);
        }
    }

    useEffect(() => {
        updateExternals();

        if (onChange && !readOnly) {
            onChange(rawQuantity)
        }
    }, [rawQuantity])

    const buttonClasses = 'relative focus:outline-none';

    return <div className={'js-form-label'}>

        <div className="relative">
            <label htmlFor={ id }>
                { label } { required ? <span style={{ color: '#C90600' }}>*</span> : null }
            </label>

            <div onMouseEnter={() => setFocused(true)} onMouseLeave={() => setFocused(false)} className={`flex styled-input ${width}`} style={{ padding: 0 }}>
                <div className={'px-1 flex-1 h-full'}>
                    <input
                        ref={ input }
                        id={ id }
                        value={ quantity }
                        name={ name }
                        onFocus={() => setFocused(true) }
                        onKeyUp={ keyUp }
                        onBlur={ defaultToOne }
                        onChange={({ target }) => parseInput(target.value)}
                        type="text"
                        pattern="[0-9.,]+"
                        readOnly={ readOnly }
                        inputMode={'numeric'}
                        style={{ minWidth: 20, lineHeight: '1em' }}
                        className={`appearance-none border-0 bg-white h-12 leading-10 w-full px-3 focus:outline-none ${defaultValue !== rawQuantity ? 'input-dirty' : 'input-clean'}`}
                    />
                </div>

                { !readOnly && <div className={`w-8 h-12 text-black border-l-2 border-grey-300 transition-opacity duration-200 ${focused ? 'opacity-100' : 'opacity-0'}`}>
                    <button type={'button'} className={`block flex justify-center items-center h-1/2 w-full text-grey-500 ${buttonClasses} ${!canIncrease ? 'opacity-25 cursor-not-allowed' : 'md:hover:bg-grey-100'}`} disabled={!canIncrease} onClick={increase}>
                        <span className={'sr-only'}>Increase</span>
                        <svg className="w-2 h-2" viewBox="64 64 896 896" width="64" height="64" fill="currentColor" aria-hidden="true"><path d="M890.5 755.3L537.9 269.2c-12.8-17.6-39-17.6-51.7 0L133.5 755.3A8 8 0 00140 768h75c5.1 0 9.9-2.5 12.9-6.6L512 369.8l284.1 391.6c3 4.1 7.8 6.6 12.9 6.6h75c6.5 0 10.3-7.4 6.5-12.7z"></path></svg>
                    </button>
                    <div className={'h-px w-full bg-grey-300'}>&nbsp;</div>
                    <button type={'button'} className={`block flex justify-center items-center h-1/2 w-full text-grey-500 ${buttonClasses} ${!canReduce ? 'opacity-25 cursor-not-allowed' : 'md:hover:bg-grey-100'}`} disabled={!canReduce} onClick={reduce} >
                        <span className={'sr-only'}>Decrease</span>
                        <svg className="w-2 h-2" viewBox="64 64 896 896" width="64" height="64" fill="currentColor" aria-hidden="true"><path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z"></path></svg>
                    </button>
                </div> }

            </div>
        </div>
    </div>
};

export default NumberInput
